import {
  BugReport,
  Demo,
  Feedback,
  Header,
  IconBorderText,
  IconButton,
  Shortcuts,
} from '@sketch-note/design-system/src/components/quick-help';
import { ChevronRightIcon } from '@sketchnote/icons';
import Link from 'next/link';
import * as React from 'react';
import { useIntercom } from 'react-use-intercom';

import { actionLinks, demos, navigations, shortcutSections } from './data';

const { useState } = React;

type Navigations = 'feedback' | 'bug-report' | 'shortcuts' | 'reset-onboarding' | 'demos' | null;

export const QuickHelp = ({
  onClose,
  onClickReset,
}: {
  onClose: () => void;
  onClickReset: () => void;
}): JSX.Element => {
  const [navigateTo, setNavigateTo] = useState<Navigations>(null);
  const [currentDemo, setCurrentDemo] = useState<any>(null);

  const { show } = useIntercom();

  const translations = {
    quick_help_title: 'Quick Help',
    quick_help_back_text: 'Back',
    quick_help_search: 'Search',
    quick_help_demo_title: 'Demos',
    send_feedback_title: 'Send feedback',
    send_feedback_desc: 'Have feedback to help make sketchnote better? Tell us!',
    send_feedback_label: 'Bouquets and brickbats',
    send_feedback_placeholder: 'Have an issue? A feature request? A compliment? Tell us here...',
    attachments_title: 'Attachments',
    attachments_desc:
      'If you have a screenshot that can help us understand the issue better, please attach it here',
    attachments_label: 'Attach files',
    attachments_drop_label: 'Drop your file here',
    feedback_question: 'How disappointed would you feel if you could no longer use Sketchnote?',
    feedback_action_btn: 'Send feedback',
    bug_report_title: 'Bug report',
    bug_report_desc: 'Have you found a problem? Please let us know!',
    bug_issue_label: "What's the issue",
    bug_explain_title: 'Description',
    bug_explain_desc:
      "Tell us what you know so far about this issue and we'll try to get it sorted ASAP!",
    bug_explain_label: 'Please describe the bug in detail',
    bug_attachements_title: 'Attachments',
    bug_attachements_desc:
      'If you have a screenshot that can help us understand the issue better, please attach it here',
    bug_action_btn: 'Send feedback',
    video_thumbnail_label: 'Play and discover added features and functionality',
    more_action_btn_label: 'More info',
    demo_module_title: 'How modules works',
    demo_lesson_title: 'How lessons works',
    demo_creation_title: 'How templates works',
    demo_document_title: 'How documents works',
    demo_module_description:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nam tincidunt semper placerat. Praesent lacus nunc, suscipit sit amet lectus in, vestibulum commodo ipsum. Sed vel mauris eget turpis iaculis suscipit volutpat vel risus.',
    demo_lesson_description:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nam tincidunt semper placerat. Praesent lacus nunc, suscipit sit amet lectus in, vestibulum commodo ipsum. Sed vel mauris eget turpis iaculis suscipit volutpat vel risus.',
    demo_creation_description:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nam tincidunt semper placerat. Praesent lacus nunc, suscipit sit amet lectus in, vestibulum commodo ipsum. Sed vel mauris eget turpis iaculis suscipit volutpat vel risus.',
    demo_document_description:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nam tincidunt semper placerat. Praesent lacus nunc, suscipit sit amet lectus in, vestibulum commodo ipsum. Sed vel mauris eget turpis iaculis suscipit volutpat vel risus.',
    shortcut_bold_label: 'Bold',
    shortcut_cursiva_label: 'Cursiva',
    shortcut_underline_label: 'Underline',
    shortcut_add_lint_to_text_label: 'Add link to text',
    shortcut_heading_1_label: 'Heading 1',
    shortcut_heading_2_label: 'Heading 2',
    shortcut_heading_3_label: 'Heading 3',
    shortcut_quote_label: 'Quote',
    shortcut_code_label: 'Code',
  };

  const handleNavigation = (navigate: Navigations, demoIndex?: number) => {
    setNavigateTo(navigate);
    if (navigate === 'demos' && demoIndex !== undefined) {
      setCurrentDemo(demos[demoIndex]);
    }
  };
  return (
    <div>
      <div className="h-60 bg-white sticky top-0 z-10 px-24">
        {navigateTo === null ? (
          <Header title={translations.quick_help_title} onCloseClick={onClose} />
        ) : (
          <Header
            title={translations.quick_help_back_text}
            onBackClick={handleNavigation.bind(this, null)}
            onCloseClick={onClose}
          />
        )}
      </div>
      <div className="px-24">
        {navigateTo ? (
          navigateTo === 'feedback' ? (
            <Feedback translations={translations} />
          ) : navigateTo === 'bug-report' ? (
            <BugReport translations={translations} />
          ) : navigateTo === 'demos' && currentDemo ? (
            <Demo
              title={translations[currentDemo.title]}
              icon={currentDemo.icon}
              description={translations[currentDemo.description]}
              thumbnailUrl={currentDemo.thumbnailUrl}
              translations={translations}
              videoId={currentDemo.videoId}
              onClose={onClose}
            />
          ) : navigateTo === 'shortcuts' ? (
            <Shortcuts shortcutSections={shortcutSections} />
          ) : null
        ) : (
          <React.Fragment>
            {/* <Input
            placeholder={'Search'}
            inputClassName={classNames({
              'text-gray-600 font-medium': true,
              [styles.input]: true
            })}
            wrapperClassName="py-14 bg-gray-100 border-none mb-24"
            field="search"
            leftIcon={<SearchIcon wrapperClass="text-gray-600 flex items-center" />}
            onChange={() => null}
          /> */}
            <div className="mb-40">
              {actionLinks?.map((link, index) =>
                link.href ? (
                  <Link href={link.href} key={`${link.href}-${index}`}>
                    <a target="_blank">
                      <IconBorderText text={link.label} icon={link.icon} />
                    </a>
                  </Link>
                ) : (
                  <IconBorderText text={link.label} icon={link.icon} />
                )
              )}
              {navigations.map((navigation, index) => (
                <IconBorderText
                  key={`${navigation.value}-${index}`}
                  text={navigation.label}
                  icon={<ChevronRightIcon />}
                  onClick={handleNavigation.bind(this, navigation.value) as () => void}
                />
              ))}
              <IconBorderText
                text="Reset onboarding"
                icon={<ChevronRightIcon />}
                onClick={onClickReset}
              />
              <IconBorderText
                text="Message Sketchnote"
                icon={<ChevronRightIcon />}
                onClick={() => show()}
              />
            </div>
            <p className="text-sm text-black mb-22">{translations.quick_help_demo_title}</p>
            {demos.map(({ label, value, icon }, index) => (
              <IconButton
                label={label}
                icon={icon}
                key={`${value}-${index}`}
                onClick={handleNavigation.bind(this, 'demos', index)}
              />
            ))}
          </React.Fragment>
        )}
      </div>
    </div>
  );
};
